import { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
} from "@mui/material";

const AddCategoryForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    category_name: "",
    category_order: "",
    category_type: "",
    active: true,
  });
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const formDataToSend = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });

      // Append image if selected
      if (image) {
        formDataToSend.append("category_image", image);
      } else {
        showSnackbar("Please select an image", "error");
        setSaving(false);
        return;
      }

      await onSubmit(formDataToSend);
      showSnackbar("Category added successfully");
    } catch (error) {
      console.error("Error adding category:", error);
      showSnackbar("Failed to add category", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h5" gutterBottom>
        Add New Category
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxWidth: 600,
          }}
        >
          <TextField
            label="Category Name"
            name="category_name"
            value={formData.category_name}
            onChange={handleInputChange}
            required
            fullWidth
          />

          <TextField
            label="Order"
            name="category_order"
            type="number"
            value={formData.category_order}
            onChange={handleInputChange}
            required
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Category Type</InputLabel>
            <Select
              name="category_type"
              value={formData.category_type}
              onChange={handleInputChange}
              label="Category Type"
              required
            >
              <MenuItem value="income">Income</MenuItem>
              <MenuItem value="expenses">Expenses</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, active: e.target.checked }))
                }
                name="active"
              />
            }
            label="Active"
          />

          <Box>
            <input
              accept="image/*"
              type="file"
              id="image-upload"
              onChange={handleImageChange}
              style={{ display: "none" }}
              required
            />
            <label htmlFor="image-upload">
              <Button variant="contained" component="span">
                Upload Image
              </Button>
            </label>
            {previewUrl && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    borderRadius: "4px",
                  }}
                />
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={saving}
              fullWidth
            >
              {saving ? "Adding..." : "Add Category"}
            </Button>
          </Box>
        </Box>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddCategoryForm;
